import { useUnitsFormatter } from "hooks/formatters";
import { Layout, PlotData } from "plotly.js";
import { useMemo } from "react";
import Plot from "components/plotly/LocalisedPlot";
import { LegendHint } from "../LegendHint";
import {
  ProductGroupName,
  ProductionType,
  TimeAggregation,
} from "src/store/api/generatedApi";
import {
  defaultLayout,
  getColorBySource,
  setSelectedDateFromPlotClick,
} from "../plotting";
import {
  GroupedProductionSeries,
  ProductionComparison,
  ProductionDenominator,
  ProductionSource,
  ProductionTimeSeries,
  ProductionTimeSeriesKey,
} from "../types";
import { TFunction } from "i18next";
import { typeSafeObjectKeys } from "src/utils/typeSafeObjectKeys";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";
import { useTranslation } from "react-i18next";

interface ProductionPlotProps {
  productionSeries: GroupedProductionSeries;
  selectedTimeAggregation: TimeAggregation;
  selectedProductionComparison: ProductionComparison;
  selectedProductionType: ProductionType;
  selectedProductionDenominator: ProductionDenominator;
  selectedProductionSource: ProductionSource | null;
  selectedProductionSources: ProductionSource[];
  selectedProductGroups: ProductGroupName[];
  setSelectedDate: (date: Date) => void;
}

export const ProductionPlot = ({
  productionSeries,
  selectedTimeAggregation,
  selectedProductionComparison,
  selectedProductionType,
  selectedProductionDenominator,
  selectedProductionSource,
  selectedProductionSources,
  selectedProductGroups,
  setSelectedDate,
}: ProductionPlotProps) => {
  const { t } = useTranslation();
  const units = useUnitsFormatter(true);

  const layout: Partial<Layout> = useMemo(() => {
    return {
      ...defaultLayout,
      yaxis: {
        ...defaultLayout.yaxis,
        title: {
          text:
            selectedProductionDenominator === "perHeat"
              ? `${t("mass")} ${units("mass")} / ${t("heat")}`
              : `${t("mass")} ${units("mass")}`,
        },
      },
    };
  }, [t, units, selectedProductionDenominator]);

  const data = useMemo(
    () =>
      buildFigData(
        productionSeries,
        selectedTimeAggregation,
        selectedProductionComparison,
        selectedProductionType,
        selectedProductionDenominator,
        selectedProductionSource,
        selectedProductionSources,
        selectedProductGroups,
        t
      ),
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- selectedTimeAggregation is passed to buildFigData but is excluded to avoid recomputing plot data on aggregation change alone
    [
      productionSeries,
      selectedProductionComparison,
      selectedProductionType,
      selectedProductionDenominator,
      selectedProductionSource,
      selectedProductionSources,
      selectedProductGroups,
      t,
    ]
  );

  return (
    <div style={{ width: "100%" }}>
      <Plot
        data={data}
        layout={layout}
        style={{ width: "100%" }}
        useResizeHandler
        onClick={setSelectedDateFromPlotClick(setSelectedDate)}
      />
      <LegendHint />
    </div>
  );
};

export const perHeatOrNull = (
  mass: number,
  heatCount: number
): number | null => (heatCount > 0 ? mass / heatCount : null);

const buildFigData = (
  productionSeries: GroupedProductionSeries,
  selectedTimeAggregation: TimeAggregation,
  selectedProductionComparison: ProductionComparison,
  selectedProductionType: ProductionType,
  selectedProductionDenominator: ProductionDenominator,
  selectedProductionSource: ProductionSource | null,
  selectedProductionSources: ProductionSource[],
  selectedProductGroups: ProductGroupName[],
  t: TFunction
): Partial<PlotData>[] => {
  const filteredItems = productionSeries.items.filter(
    ([key]) =>
      key.productionType == selectedProductionType &&
      key.timeAggregation == selectedTimeAggregation
  );
  const sourceFilteredItems = productionSeries.items.filter(
    ([key]) =>
      key.productionType == selectedProductionType &&
      key.timeAggregation == selectedTimeAggregation &&
      key.productionSource == selectedProductionSource
  );
  switch (selectedProductionComparison) {
    case "sources":
      return buildSourceComparisonData(
        filteredItems,
        selectedProductionSources,
        selectedProductionDenominator,
        t
      );
    case "productGroups":
      return buildProductGroupsComparisonData(
        sourceFilteredItems,
        selectedProductGroups,
        selectedProductionDenominator,
        t
      );
  }
};

export const buildSourceComparisonData = (
  keyedSeries: [ProductionTimeSeriesKey, ProductionTimeSeries][],
  availableProductionSources: ProductionSource[],
  selectedProductionDenominator: ProductionDenominator,
  t: TFunction
): Partial<PlotData>[] => {
  return availableProductionSources.map((source) => {
    const filteredSeries = keyedSeries
      .filter(([key]) => key.productionSource == source)
      .map(([, series]) => series);
    const dates = Array.from(
      new Set(filteredSeries.flatMap(({ date }) => date.map((value) => value)))
    ).sort();
    const initialDateTotals = typeSafeObjectFromEntries(
      dates.map((date) => [date, 0])
    );
    const dateMasses = filteredSeries.reduce(
      (previous, current) => ({
        ...previous,
        ...typeSafeObjectFromEntries(
          current.date.map((date, i) => [
            date,
            (previous[date] ?? 0) + (current.produced_mass[i] ?? 0),
          ])
        ),
      }),
      initialDateTotals
    );
    const dateHeats = filteredSeries.reduce(
      (previous, current) => ({
        ...previous,
        ...typeSafeObjectFromEntries(
          current.date.map((date, i) => [
            date,
            (previous[date] ?? 0) + (current.heat_count[i] ?? 0),
          ])
        ),
      }),
      initialDateTotals
    );

    const plotDates = typeSafeObjectKeys(dateMasses);
    return {
      x: plotDates,
      y: plotDates.map((date) =>
        selectedProductionDenominator === "perHeat"
          ? perHeatOrNull(dateMasses[date] ?? 0, dateHeats[date] ?? 0)
          : (dateMasses[date] ?? 0)
      ),
      type: "scatter",
      mode: "lines+markers",
      name: t(source),
      line: {
        color: getColorBySource(availableProductionSources, source),
      },
    };
  });
};

export const buildProductGroupsComparisonData = (
  keyedSeries: [ProductionTimeSeriesKey, ProductionTimeSeries][],
  selectedProductGroups: ProductGroupName[],
  selectedProductionDenominator: ProductionDenominator,
  t: TFunction
): Partial<PlotData>[] => {
  return selectedProductGroups.map((productGroup) => {
    const filteredKeyedSeries = keyedSeries.find(
      ([key]) => key.productGroup == productGroup
    );
    if (filteredKeyedSeries === undefined) {
      return {};
    }
    const [, series] = filteredKeyedSeries;
    const perHeat = selectedProductionDenominator === "perHeat";
    return {
      x: series.date,
      y: perHeat
        ? series.produced_mass.map((mass, i) =>
            perHeatOrNull(mass, series.heat_count[i] ?? 0)
          )
        : series.produced_mass,
      type: "scatter",
      mode: "lines+markers",
      name: t(productGroup),
      line: {
        color: getColorBySource(selectedProductGroups, productGroup),
      },
      // Per-heat is an average; stacking averages across product groups is not
      // meaningful.
      ...(perHeat ? {} : { stackgroup: `productGroupMass` }),
    };
  });
};
